import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosWithAuth from '../../UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../../Constants/apiRoutes';
import { showFeedback } from '../../UI/uiFeedbackSlice';

/* ============================
   Thunks (API calls)
   ============================ */

// List (with optional filters & pagination)
export const fetchExpensesByProject = createAsyncThunk(
  'expenses/fetchByProject',
  async ({ projectId, page = 1, pageSize = 20, filters = {} }, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;


    if (!projectId) {
      return rejectWithValue('projectId is required for fetchExpensesByProject');
    }



    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (filters.category) params.set('category', filters.category);
      if (filters.hasBill !== undefined) params.set('hasBill', String(filters.hasBill));
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.q) params.set('q', filters.q);
      if (filters.sort) params.set('sort', filters.sort);

      const url = `${API_ROUTES.GET_EXPENSES_BY_PROJECT(projectId)}?${params.toString()}`;
      const res = await axiosWithAuth.get(url);
      return { projectId, ...res.data.data }; // { items, total, page, pageSize }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch expenses';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load expenses',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Create
export const createExpense = createAsyncThunk(
  'expenses/create',
  async (payload, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      // payload = { projectId, category, amount, notes, date, paymentMethod, billNumber }
      const res = await axiosWithAuth.post(API_ROUTES.CREATE_EXPENSE, payload);
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Expense added',
          message: 'Your expense has been recorded.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to add expense';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Expense creation failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Upload / replace bill
export const uploadExpenseBill = createAsyncThunk(
  'expenses/uploadBill',
  async ({ expenseId, fileUrl, fileName, mimeType, billNumber }, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.post(
        API_ROUTES.UPLOAD_EXPENSE_BILL(expenseId),
        { fileUrl, fileName, mimeType, billNumber }
      );
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Bill uploaded',
          message: 'The bill has been attached to this expense.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to upload bill';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Bill upload failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Delete bill
export const deleteExpenseBill = createAsyncThunk(
  'expenses/deleteBill',
  async (expenseId, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.delete(API_ROUTES.DELETE_EXPENSE_BILL(expenseId));
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Bill removed',
          message: 'The bill has been detached from this expense.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete bill';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Bill delete failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Get one (drawer)
export const fetchExpenseById = createAsyncThunk(
  'expenses/fetchById',
  async (expenseId, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.get(API_ROUTES.GET_EXPENSE_BY_ID(expenseId));
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch expense details';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load expense',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Edit
export const editExpense = createAsyncThunk(
  'expenses/edit',
  async ({ expenseId, updates }, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.patch(API_ROUTES.EDIT_EXPENSE(expenseId), updates);
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Expense updated',
          message: 'Changes have been saved.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update expense';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Update failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Soft delete
export const deleteExpense = createAsyncThunk(
  'expenses/delete',
  async (expenseId, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.delete(API_ROUTES.DELETE_EXPENSE(expenseId));

      // backend returns just message; we still need to remove locally
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Expense deleted',
          message: 'The expense has been removed.',
        })
      );
      return { expenseId };
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete expense';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Delete failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Overview cards
export const fetchExpenseOverview = createAsyncThunk(
  'expenses/fetchOverview',
  async (projectId, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const res = await axiosWithAuth.get(API_ROUTES.GET_EXPENSE_OVERVIEW(projectId));
      return res.data.data; // { totalExpenses, pendingBills, byCategory: [{category,total,count}] }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch expense overview';
      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load overview',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

// Report (JSON rows for now; export handled in UI or a separate endpoint)
export const fetchExpenseReport = createAsyncThunk(
  'expenses/fetchReport',
  async ({ projectId, dateFrom, dateTo, category }, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (category) params.set('category', category);
      const res = await axiosWithAuth.get(API_ROUTES.GET_EXPENSE_REPORT(projectId, params.toString()));
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Report ready',
          message: 'Expense report has been generated.',
        })
      );

      return res.data.data; // { rows }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch expense report';
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Report failed',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);


// Recent activity
export const fetchRecentExpenseActivity = createAsyncThunk(
  'expenses/fetchRecentActivity',
  async ({ projectId, limit = 6 }, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      const qs = `types=expense_create,expense_bill,expense_edit,expense_delete&limit=${limit}`;
      const res = await axiosWithAuth.get(API_ROUTES.GET_EXPENSE_ACTIVITY(projectId, qs));
      return res.data.data; // { items, nextCursor }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch activity';
      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load activity',
          message,
        })
      );
      return rejectWithValue(message);
    }
  }
);

export const fetchMoreExpenseActivity = createAsyncThunk(
  'expenses/fetchMoreActivity',
  async ({ projectId, cursor, limit = 6 }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const params = new URLSearchParams({
        types: 'expense_create,expense_bill,expense_edit,expense_delete',
        limit: String(limit),
        cursorCreatedAt: cursor.cursorCreatedAt,
        cursorId: cursor.cursorId,
      }).toString();
      const res = await axiosWithAuth.get(API_ROUTES.GET_EXPENSE_ACTIVITY(projectId, params));
      return res.data.data; // { items, nextCursor }
    } catch (err) {
      // silent on popup, just fail the state
      return rejectWithValue(
        err?.response?.data?.message || 'Failed to fetch more activity'
      );
    }
  }
);


// ⬇️ EXPORT PDF via /reports?type=expenses&...
export const exportExpensePdf = createAsyncThunk(
  'expenses/exportPdf',
  async ({ projectId, from, to, detail = 'summary' }, { dispatch, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        type: 'expenses',
        projectId,
        from,
        to,
        detail,
        format: 'pdf',
      });

      const url = `${API_ROUTES.GENERATE_REPORT}?${params.toString()}`;

      const res = await axiosWithAuth.get(url, { responseType: 'blob' });

      // 🔍 Sanity check: make sure it looks like a PDF
      const contentType = res.headers?.['content-type'] || res.headers?.['Content-Type'];
      if (!contentType || !contentType.includes('pdf')) {
        // Try to read the blob as text to see the error
        let text = '';
        if (res.data && typeof res.data.text === 'function') {
          text = await res.data.text();
        }
        console.error('Unexpected response for exportExpensePdf:', {
          status: res.status,
          contentType,
          text,
        });
        throw new Error(text || 'Unexpected response while generating report');
      }

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `site360-expense-report-${from}-to-${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Report downloaded',
          message: 'Your expense report PDF has been generated.',
        })
      );

      return true;
    } catch (err) {
      let message =
        err?.response?.data?.message || err?.message || 'Failed to generate expense report.';

      // If backend returned a blob with text, read it for debugging
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          if (text) message = text;
        } catch (_) { }
      }

      console.error('exportExpensePdf error:', err?.response?.status, message);

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Export failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);




/* ============================
   Slice
   ============================ */

const expenseSlice = createSlice({
  name: 'expenses',
  initialState: {
    // list
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
    loading: false,

    // details (drawer)
    expenseDetails: null,
    expenseDetailsLoading: false,

    // create/edit ops
    submitting: false,

    // overview cards
    overview: null,
    overviewLoading: false,

    // report
    reportRows: [],
    reportLoading: false,

    // 👇 NEW
    exportingReport: false,

    error: null,


    // activity
    expenseActivity: [],
    expenseActivityNextCursor: null,
    expenseActivityLoading: false,
  },
  reducers: {
    clearExpenseError: (state) => { state.error = null; },
    clearExpenseDetails: (state) => {
      state.expenseDetails = null;
      state.expenseDetailsLoading = false;
    },
    resetExpenseList: (state) => {
      state.items = [];
      state.total = 0;
      state.page = 1;
      state.pageSize = 20;
      state.loading = false;
    },
    resetExpenseActivity: (state) => {
      state.expenseActivity = [];
      state.expenseActivityNextCursor = null;
      state.expenseActivityLoading = false;
    },

  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(fetchExpensesByProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExpensesByProject.fulfilled, (state, action) => {
        state.loading = false;
        const { items, total, page, pageSize } = action.payload;
        state.items = items;
        state.total = total;
        state.page = page;
        state.pageSize = pageSize;
      })
      .addCase(fetchExpensesByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createExpense.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.submitting = false;
        // Prepend newest
        state.items = [action.payload, ...state.items];
        state.total += 1;
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })







      // Upload/Replace Bill
      .addCase(uploadExpenseBill.pending, (state) => {
        state.submitting = true;
      })
      .addCase(uploadExpenseBill.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;
        state.items = state.items.map((e) => (e._id === updated._id ? updated : e));
        if (state.expenseDetails?._id === updated._id) state.expenseDetails = updated;
      })
      .addCase(uploadExpenseBill.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Delete Bill
      .addCase(deleteExpenseBill.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteExpenseBill.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;
        state.items = state.items.map((e) => (e._id === updated._id ? updated : e));
        if (state.expenseDetails?._id === updated._id) state.expenseDetails = updated;
      })
      .addCase(deleteExpenseBill.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // One
      .addCase(fetchExpenseById.pending, (state) => {
        state.expenseDetailsLoading = true;
        state.expenseDetails = null;
      })
      .addCase(fetchExpenseById.fulfilled, (state, action) => {
        state.expenseDetailsLoading = false;
        state.expenseDetails = action.payload;
      })
      .addCase(fetchExpenseById.rejected, (state, action) => {
        state.expenseDetailsLoading = false;
        state.error = action.payload;
      })

      // Edit
      .addCase(editExpense.pending, (state) => {
        state.submitting = true;
      })
      .addCase(editExpense.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;
        // Update list
        state.items = state.items.map((e) => {
          if (e._id !== updated._id) return e;
          return {
            ...e,
            ...updated,
            // preserve display fields if backend didn't include them
            paidByName: updated.paidByName ?? e.paidByName,
            billUploadedByName: updated.billUploadedByName ?? e.billUploadedByName,
          };
        });

        // Update drawer if it's the same expense
        if (state.expenseDetails?._id === updated._id) {
          state.expenseDetails = {
            ...state.expenseDetails,
            ...updated,
            paidByName: updated.paidByName ?? state.expenseDetails.paidByName,
            billUploadedByName: updated.billUploadedByName ?? state.expenseDetails.billUploadedByName,
          };
        }
      })
      .addCase(editExpense.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Delete (soft)
      .addCase(deleteExpense.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.submitting = false;
        const { expenseId } = action.payload;
        state.items = state.items.filter((e) => e._id !== expenseId);
        state.total = Math.max(0, state.total - 1);
        if (state.expenseDetails?._id === expenseId) {
          state.expenseDetails = null;
        }
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Overview
      .addCase(fetchExpenseOverview.pending, (state) => {
        state.overviewLoading = true;
      })
      .addCase(fetchExpenseOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overview = action.payload; // { totalExpenses, pendingBills, byCategory: [...] }
      })
      .addCase(fetchExpenseOverview.rejected, (state, action) => {
        state.overviewLoading = false;
        state.error = action.payload;
      })

      // Report
      .addCase(fetchExpenseReport.pending, (state) => {
        state.reportLoading = true;
      })
      .addCase(fetchExpenseReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reportRows = action.payload.rows || [];
      })
      .addCase(fetchExpenseReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.error = action.payload;
      })



      // Activity
      .addCase(fetchRecentExpenseActivity.pending, (state) => {
        state.expenseActivityLoading = true;
      })
      .addCase(fetchRecentExpenseActivity.fulfilled, (state, action) => {
        state.expenseActivityLoading = false;
        state.expenseActivity = action.payload.items || [];
        state.expenseActivityNextCursor = action.payload.nextCursor || null;
      })
      .addCase(fetchRecentExpenseActivity.rejected, (state, action) => {
        state.expenseActivityLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchMoreExpenseActivity.pending, (state) => {
        state.expenseActivityLoading = true;
      })
      .addCase(fetchMoreExpenseActivity.fulfilled, (state, action) => {
        state.expenseActivityLoading = false;
        state.expenseActivity = [...state.expenseActivity, ...(action.payload.items || [])];
        state.expenseActivityNextCursor = action.payload.nextCursor || null;
      })
      .addCase(fetchMoreExpenseActivity.rejected, (state, action) => {
        state.expenseActivityLoading = false;
        state.error = action.payload;
      })


      // 👇 ADD THESE 3 CASES
      .addCase(exportExpensePdf.pending, (state) => {
        state.exportingReport = true;
      })
      .addCase(exportExpensePdf.fulfilled, (state) => {
        state.exportingReport = false;
      })
      .addCase(exportExpensePdf.rejected, (state) => {
        state.exportingReport = false;
      });

  },
});

export const {
  clearExpenseError,
  clearExpenseDetails,
  resetExpenseList,
  resetExpenseActivity
} = expenseSlice.actions;

export default expenseSlice.reducer;
